import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Container, Paper, Stack, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';

export default function EmailVerification() {
  const { currentUser, verifySession, sendVerificationEmail, logout } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // A verification link may have been completed in another tab. This read
    // never sends an email and never consumes an email action code.
    verifySession({ keepPage: true }).then((session) => {
      if (cancelled) return;
      if (session?.emailVerified) navigate(session.mfaEnrolled ? '/' : '/mfa-setup');
      setChecking(false);
    });
    return () => { cancelled = true; };
  }, [currentUser?.uid, verifySession, navigate]);

  async function sendEmail() {
    setSending(true);
    setError(null);
    try {
      await sendVerificationEmail();
      setSent(true);
      setNotice('Verification email sent. Open its link and click Verify on the website.');
    } catch (failure) {
      setError(failure.code === 'auth/too-many-requests'
        ? 'Please wait a few minutes before requesting another verification email.'
        : 'Could not send the verification email. Check your connection and try again.');
    } finally {
      setSending(false);
    }
  }

  async function checkEmail() {
    setChecking(true);
    setError(null);
    const session = await verifySession({ keepPage: true });
    if (session?.emailVerified) navigate(session.mfaEnrolled ? '/' : '/mfa-setup');
    else if (session) setNotice('Your email is not verified yet. Open the email link and click Verify.');
    setChecking(false);
  }

  async function signOut() {
    try {
      await logout();
    } catch (failure) {
      setError('Could not sign out. Reload the page and try again.');
    }
  }

  return (
    <>
      <Header />
      <Container maxWidth="sm" sx={{ mt: '6rem', mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Stack spacing={2}>
            <Typography variant="h4">Verify your email address</Typography>
            <Typography>Verify {currentUser?.email} before setting up two-factor authentication.</Typography>
            {error && <Alert severity="error">{error}</Alert>}
            {notice && <Alert severity="info">{notice}</Alert>}
            <Button variant="contained" disabled={sending || checking} onClick={sendEmail}>
              {sending ? 'Sending...' : sent ? 'Resend verification email' : 'Send verification email'}
            </Button>
            <Button disabled={checking || sending} onClick={checkEmail}>
              {checking ? 'Checking...' : 'Check verification status'}
            </Button>
            <Button disabled={checking || sending} onClick={signOut}>Sign out</Button>
          </Stack>
        </Paper>
      </Container>
    </>
  );
}
